import { useEffect, useMemo, useState } from "react";

import type {
  CatalogSelectionDimensionProjection,
  CatalogVariantProjection,
  PriceSnapshot,
} from "../runtime/gateway/catalog-price-client";
import "./VariantSelectionRuntime.css";

const NOT_APPLICABLE = "__MORE_NOT_APPLICABLE__";

type SellableVariantEntry = {
  variant: CatalogVariantProjection;
  price: PriceSnapshot;
};

type Props = {
  menuName: string;
  variants: SellableVariantEntry[];
  selectionDimensions: CatalogSelectionDimensionProjection[];
  initialVariantId?: string | null;
  mediaUrl?: string | null;
  mediaAlt?: string | null;
  mediaFitMode?: "COVER" | "CONTAIN";
  mediaFocalPoint?: { x: number; y: number } | null;
  onSelectionChange?: () => void;
  onResolve: (variant: CatalogVariantProjection, price: PriceSnapshot) => void;
};

type Choice = {
  key: string;
  label: string;
};

type DimensionBoard = {
  dimension: CatalogSelectionDimensionProjection;
  enabled: boolean;
  hidden: boolean;
  forcedKey: string | null;
  prerequisiteLabel: string | null;
  choices: Choice[];
};

function formatMoney(value: number, currency = "THB") {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function optionForDimension(variant: CatalogVariantProjection, dimensionId: string) {
  return (variant.options ?? []).find((option) => option.dimension_id === dimensionId) ?? null;
}

function variantMatchesSelections(
  variant: CatalogVariantProjection,
  selections: Record<string, string>,
) {
  return Object.keys(selections).every((dimensionId) => {
    const selectedKey = selections[dimensionId];
    const option = optionForDimension(variant, dimensionId);
    if (selectedKey === NOT_APPLICABLE) return option === null;
    return option?.option_id === selectedKey;
  });
}

function filterCandidates(
  variants: SellableVariantEntry[],
  selections: Record<string, string>,
) {
  return variants.filter((entry) => variantMatchesSelections(entry.variant, selections));
}

function orderedDimensions(dimensions: CatalogSelectionDimensionProjection[]) {
  return [...dimensions].sort((a, b) => {
    if (a.selection_stage !== b.selection_stage) return a.selection_stage - b.selection_stage;
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.dimension_id.localeCompare(b.dimension_id);
  });
}

function selectionsForStage(
  dimensions: CatalogSelectionDimensionProjection[],
  selections: Record<string, string>,
  stage: number,
  excludedDimensionId?: string,
) {
  const result: Record<string, string> = {};
  for (const dimension of dimensions) {
    if (dimension.selection_stage > stage) continue;
    if (dimension.dimension_id === excludedDimensionId) continue;
    const value = selections[dimension.dimension_id];
    if (value !== undefined) result[dimension.dimension_id] = value;
  }
  return result;
}

function earlierStagesComplete(
  dimensions: CatalogSelectionDimensionProjection[],
  selections: Record<string, string>,
  stage: number,
) {
  return dimensions
    .filter((dimension) => dimension.selection_stage < stage)
    .every((dimension) => selections[dimension.dimension_id] !== undefined);
}

function choicesForDimension(
  variants: SellableVariantEntry[],
  dimension: CatalogSelectionDimensionProjection,
  selections: Record<string, string>,
) {
  const candidates = filterCandidates(variants, selections);
  const byKey = new Map<
    string,
    { label: string; sortOrder: number | null; discoveryOrder: number }
  >();
  let discoveryOrder = 0;

  for (const entry of candidates) {
    const option = optionForDimension(entry.variant, dimension.dimension_id);
    const key = option?.option_id ?? NOT_APPLICABLE;
    if (!byKey.has(key)) {
      const rawSortOrder = option
        ? (option as typeof option & { sort_order?: unknown }).sort_order
        : null;
      byKey.set(key, {
        label: option?.option_name ?? "",
        sortOrder: typeof rawSortOrder === "number" ? rawSortOrder : null,
        discoveryOrder,
      });
      discoveryOrder += 1;
    }
  }

  return Array.from(byKey.entries())
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => {
      const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.discoveryOrder - b.discoveryOrder;
    })
    .map(({ key, label }) => ({ key, label }));
}

function completeForcedSelections(
  variants: SellableVariantEntry[],
  dimensions: CatalogSelectionDimensionProjection[],
  selections: Record<string, string>,
) {
  const next = { ...selections };
  const stages = Array.from(new Set(dimensions.map((dimension) => dimension.selection_stage)))
    .sort((a, b) => a - b);

  let changed = true;
  while (changed) {
    changed = false;

    for (const stage of stages) {
      if (!earlierStagesComplete(dimensions, next, stage)) break;

      const stageDimensions = dimensions.filter((dimension) => dimension.selection_stage === stage);
      for (const dimension of stageDimensions) {
        if (next[dimension.dimension_id] !== undefined) continue;
        const base = selectionsForStage(dimensions, next, stage, dimension.dimension_id);
        const choices = choicesForDimension(variants, dimension, base);
        if (choices.length === 1) {
          next[dimension.dimension_id] = choices[0].key;
          changed = true;
        }
      }

      const stageComplete = stageDimensions.every(
        (dimension) => next[dimension.dimension_id] !== undefined,
      );
      if (!stageComplete) break;
    }
  }

  return next;
}

function selectionsForVariant(
  variant: CatalogVariantProjection,
  dimensions: CatalogSelectionDimensionProjection[],
) {
  const selections: Record<string, string> = {};
  for (const dimension of dimensions) {
    selections[dimension.dimension_id] =
      optionForDimension(variant, dimension.dimension_id)?.option_id ?? NOT_APPLICABLE;
  }
  return selections;
}

function selectionLabel(
  variants: SellableVariantEntry[],
  dimension: CatalogSelectionDimensionProjection,
  selectedKey: string,
) {
  if (selectedKey === NOT_APPLICABLE) return null;
  const option = variants
    .flatMap((entry) => entry.variant.options ?? [])
    .find((candidate) =>
      candidate.dimension_id === dimension.dimension_id
      && candidate.option_id === selectedKey,
    );
  return option?.option_name ?? dimension.dimension_name;
}

export function VariantSelectionRuntime({
  menuName,
  variants,
  selectionDimensions,
  initialVariantId,
  mediaUrl,
  mediaAlt,
  mediaFitMode = "COVER",
  mediaFocalPoint,
  onSelectionChange,
  onResolve,
}: Props) {
  const dimensions = useMemo(() => {
    return orderedDimensions(selectionDimensions).filter((dimension) => {
      const keys = new Set(
        variants.map((entry) => optionForDimension(entry.variant, dimension.dimension_id)?.option_id ?? NOT_APPLICABLE),
      );
      return keys.size > 1 || !keys.has(NOT_APPLICABLE);
    });
  }, [selectionDimensions, variants]);

  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const initialVariant = initialVariantId
      ? variants.find((entry) => entry.variant.variant_id === initialVariantId)?.variant ?? null
      : null;
    return completeForcedSelections(
      variants,
      dimensions,
      initialVariant ? selectionsForVariant(initialVariant, dimensions) : {},
    );
  });

  useEffect(() => {
    setSelections((current) => {
      const stillValid = filterCandidates(variants, current).length > 0;
      return completeForcedSelections(
        variants,
        dimensions,
        stillValid ? current : {},
      );
    });
  }, [dimensions, variants]);

  const candidates = useMemo(
    () => filterCandidates(variants, selections),
    [selections, variants],
  );

  const boards = useMemo<DimensionBoard[]>(() => {
    return dimensions.map((dimension) => {
      const enabled = earlierStagesComplete(
        dimensions,
        selections,
        dimension.selection_stage,
      );
      const missingPrevious = dimensions.find((candidate) =>
        candidate.selection_stage < dimension.selection_stage
        && selections[candidate.dimension_id] === undefined,
      ) ?? null;
      const base = selectionsForStage(
        dimensions,
        selections,
        dimension.selection_stage,
        dimension.dimension_id,
      );
      const rawChoices = enabled ? choicesForDimension(variants, dimension, base) : [];
      const forcedKey = rawChoices.length === 1 ? rawChoices[0].key : null;

      return {
        dimension,
        enabled,
        hidden: enabled && forcedKey === NOT_APPLICABLE,
        forcedKey,
        prerequisiteLabel: missingPrevious?.dimension_name ?? null,
        choices: rawChoices.filter((choice) => choice.key !== NOT_APPLICABLE),
      };
    });
  }, [dimensions, selections, variants]);

  const selectedTrail = useMemo(() => {
    return dimensions.flatMap((dimension) => {
      const selectedKey = selections[dimension.dimension_id];
      if (selectedKey === undefined) return [];
      const label = selectionLabel(variants, dimension, selectedKey);
      return label ? [{ dimension, label }] : [];
    });
  }, [dimensions, selections, variants]);

  const selectionComplete = dimensions.every(
    (dimension) => selections[dimension.dimension_id] !== undefined,
  );

  function choose(dimension: CatalogSelectionDimensionProjection, choice: Choice) {
    if (!earlierStagesComplete(dimensions, selections, dimension.selection_stage)) return;

    onSelectionChange?.();

    const base = selectionsForStage(
      dimensions,
      selections,
      dimension.selection_stage,
      dimension.dimension_id,
    );

    for (const [dimensionId, selectedKey] of Object.entries(base)) {
      if (selectedKey === NOT_APPLICABLE) delete base[dimensionId];
    }

    const currentlySelected = selections[dimension.dimension_id] === choice.key;
    if (!currentlySelected) base[dimension.dimension_id] = choice.key;

    const completed = completeForcedSelections(variants, dimensions, base);
    const nextCandidates = filterCandidates(variants, completed);
    const completedAll = dimensions.every(
      (item) => completed[item.dimension_id] !== undefined,
    );

    setSelections(completed);

    if (completedAll && nextCandidates.length === 1) {
      onResolve(nextCandidates[0].variant, nextCandidates[0].price);
    }
  }

  function reset() {
    onSelectionChange?.();
    setSelections(completeForcedSelections(variants, dimensions, {}));
  }

  if (!dimensions.length) {
    return (
      <div className="variant-board variant-board--fallback">
        <div className="variant-board__selection-panel">
          <div className="variant-board__selection-header">
            <div>
              <span>รูปแบบที่ขายได้</span>
              <strong>{menuName}</strong>
            </div>
            <b>{variants.length} แบบ</b>
          </div>
          <div className="variant-board__exact-grid">
            {variants.map(({ variant, price }) => (
              <button
                type="button"
                className="variant-board__exact-card"
                key={variant.variant_id}
                onClick={() => onResolve(variant, price)}
              >
                <span>
                  <strong>{variant.variant_label || variant.variant_code}</strong>
                  <small>{variant.variant_code}</small>
                </span>
                <b>{formatMoney(price.final_price, price.currency_code)}</b>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="variant-board">
      <div className="variant-board__content">
        <div className="variant-board__selection-panel">
          <div className="variant-board__selection-header">
            <div>
              <span>รูปแบบที่กำลังเลือก</span>
              <strong>{selectedTrail.length ? selectedTrail.map((item) => item.label).join(" · ") : `เลือกตัวเลือกของ ${menuName}`}</strong>
            </div>
            <b>{variants.length} แบบที่ขายได้</b>
          </div>

          <div className="variant-board__groups" aria-label={`ตัวเลือก ${menuName}`}>
            {boards.filter((board) => !board.hidden).map((board) => {
              const selectedKey = selections[board.dimension.dimension_id];
              const selectedLabel = selectedKey === undefined
                ? null
                : selectionLabel(variants, board.dimension, selectedKey);

              return (
                <section
                  className={`variant-board__group ${board.enabled ? "variant-board__group--enabled" : "variant-board__group--locked"}`}
                  key={board.dimension.dimension_id}
                >
                  <div className="variant-board__group-label">
                    <strong>{board.dimension.dimension_name}</strong>
                    <span>
                      {selectedLabel
                        ?? (board.enabled
                          ? `เลือก${board.dimension.dimension_name}`
                          : `เลือก ${board.prerequisiteLabel ?? "ตัวเลือกขั้นก่อนหน้า"} ก่อน`)}
                    </span>
                  </div>

                  <div className="variant-board__choices">
                    {board.enabled ? board.choices.map((choice) => {
                      const selected = selectedKey === choice.key;
                      const forced = board.forcedKey === choice.key && selected;
                      return (
                        <button
                          type="button"
                          className={`variant-board__choice ${selected ? "variant-board__choice--selected" : ""}`}
                          key={choice.key}
                          disabled={forced}
                          onClick={() => choose(board.dimension, choice)}
                          aria-pressed={selected}
                          aria-label={forced ? `${choice.label} เลือกอัตโนมัติ` : choice.label}
                        >
                          <strong>{choice.label}</strong>
                          {forced ? <small>อัตโนมัติ</small> : null}
                        </button>
                      );
                    }) : (
                      <div className="variant-board__locked-copy">เลือกตัวเลือกขั้นก่อนหน้าก่อน</div>
                    )}
                  </div>
                </section>
              );
            })}

            {selectionComplete && candidates.length > 1 ? (
              <section className="variant-board__group variant-board__exact">
                <div className="variant-board__group-label">
                  <strong>แบบที่ขายได้</strong>
                  <span>Combination นี้ยังตรงกับหลาย Variant</span>
                </div>
                <div className="variant-board__exact-grid">
                  {candidates.map(({ variant, price }) => (
                    <button
                      type="button"
                      className="variant-board__exact-card"
                      key={variant.variant_id}
                      onClick={() => onResolve(variant, price)}
                    >
                      <span><strong>{variant.variant_label || variant.variant_code}</strong></span>
                      <b>{formatMoney(price.final_price, price.currency_code)}</b>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <footer className="variant-board__footer">
            <span>เหลือ <strong>{candidates.length}</strong> แบบที่ตรงกับตัวเลือก</span>
            <button type="button" onClick={reset} disabled={!selectedTrail.length}>↻ ล้างตัวเลือก</button>
          </footer>
        </div>

        <aside className="variant-board__media" aria-label="พื้นที่รูปสินค้า">
          {mediaUrl ? (
            <div className="variant-board__media-image" data-fit-mode={mediaFitMode}>
              <img
                src={mediaUrl}
                alt={mediaAlt?.trim() || menuName}
                draggable={false}
                style={mediaFocalPoint ? {
                  objectPosition: `${mediaFocalPoint.x * 100}% ${mediaFocalPoint.y * 100}%`,
                } : undefined}
              />
            </div>
          ) : (
            <div className="variant-board__media-placeholder">
              <div aria-hidden="true">▧</div>
              <strong>รูปสินค้า</strong>
              <span>กำลังใช้รูปหลักจาก Published Catalog / Local Media</span>
            </div>
          )}
          {selectedTrail.length ? (
            <div className="variant-board__selected-summary">
              <span>ที่เลือกแล้ว</span>
              <strong>{selectedTrail.map((item) => item.label).join(" · ")}</strong>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
